import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Coins,
  ArrowRight,
  Shield,
  Landmark,
  Target,
  PiggyBank,
} from "lucide-react";
import { api } from "@/lib/api";
import { Widget, WidgetEmpty } from "@/components/dashboard/Widget";
import { TrendPill } from "@/components/dashboard/TrendPill";
import { Button } from "@/components/ui/button";
import { useCurrency, useCurrencyChangeListener } from "@/context/CurrencyContext";

/** Financial Snapshot — net worth + 4 key financial health stats. */
export const FinancialSnapshotWidget = ({ size = "lg" }) => {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: false });
  const { currency: userCurrency, format: userFormat, ready: currencyReady } = useCurrency();

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    return api
      .get("/dashboard/financial-snapshot")
      .then((r) => setState({ loading: false, data: r.data, error: false }))
      .catch(() => setState({ loading: false, data: null, error: true }));
  };

  useEffect(() => {
    let alive = true;
    api
      .get("/dashboard/financial-snapshot")
      .then((r) => alive && setState({ loading: false, data: r.data, error: false }))
      .catch(() => alive && setState({ loading: false, data: null, error: true }));
    return () => { alive = false; };
  }, []);

  // Refetch after profile-currency change so cached numbers use the new ccy.
  useCurrencyChangeListener(load);

  const d = state.data;
  // Prefer backend-provided currency (from the user's plan/profile). Fall
  // back to the resolved user currency, never a hardcoded "USD".
  const ccy = d?.currency || userCurrency;
  const locale = typeof navigator !== "undefined" ? navigator.language : "en-US";

  const currencyFmt = (value) => {
    if (value == null) return "—";
    if (!currencyReady && !d?.currency) return "…";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ccy,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return userFormat(value, { currency: ccy, decimals: 0 });
    }
  };

  const compactFmt = (value) => {
    if (value == null) return "—";
    if (!currencyReady && !d?.currency) return "…";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ccy,
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value);
    } catch {
      return userFormat(value, { currency: ccy, compact: true });
    }
  };

  return (
    <Widget
      title="Financial Snapshot"
      icon={Coins}
      size={size}
      testId="financial-snapshot-widget"
      viewAllTo="/app/financial"
      viewAllLabel="Full plan"
    >
      {state.loading && (
        <div className="space-y-4">
          <div className="skeleton h-8 w-40" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2 rounded-lg border border-border p-3">
                <div className="skeleton h-3 w-20" />
                <div className="skeleton h-6 w-16" />
                <div className="skeleton h-1.5 w-full" />
              </div>
            ))}
          </div>
        </div>
      )}

      {!state.loading && (state.error || !d) && (
        <div className="text-sm text-muted-foreground text-center py-4">
          Couldn&apos;t load your snapshot.
        </div>
      )}

      {d?.state === "no_plan" && (
        <WidgetEmpty
          testId="financial-snapshot-empty"
          title="No financial plan yet"
          description="Track net worth, savings rate, emergency fund, and retirement readiness in one place."
          action={
            <Button
              size="sm"
              onClick={() => nav(d.cta?.path || "/app/financial")}
              className="gap-1.5"
              data-testid="financial-snapshot-empty-cta"
            >
              {d.cta?.label || "Create financial plan"}{" "}
              <ArrowRight size={13} strokeWidth={2} />
            </Button>
          }
        />
      )}

      {d?.state === "ok" && (
        <>
          {/* Net worth header */}
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                Net worth
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className="font-display font-black text-3xl text-foreground"
                  data-testid="financial-snapshot-net-worth"
                >
                  {currencyFmt(d.net_worth)}
                </span>
                {d.trend?.net_worth && (
                  <TrendPill
                    delta={d.trend.net_worth.delta}
                    period={
                      d.trend.net_worth.period_days
                        ? `${d.trend.net_worth.period_days}d`
                        : undefined
                    }
                    format="currency"
                    currencyFormat={compactFmt}
                    testId="financial-snapshot-net-worth-trend"
                  />
                )}
              </div>
            </div>
            {d.financial_health_score != null && (
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                  Health score
                </div>
                <div
                  className="font-display font-bold text-2xl text-foreground"
                  data-testid="financial-snapshot-health-score"
                >
                  {Math.round(d.financial_health_score)}%
                </div>
              </div>
            )}
          </div>

          {/* 4 stat cards */}
          <div className="grid grid-cols-2 gap-3">
            <StatCell
              icon={PiggyBank}
              label="Savings rate"
              value={`${Math.round(d.savings_rate_pct)}%`}
              subvalue={`target ${Math.round(d.savings_rate_target_pct)}%`}
              progressPct={Math.min(
                100,
                (d.savings_rate_pct / d.savings_rate_target_pct) * 100,
              )}
              tone={
                d.savings_rate_pct >= d.savings_rate_target_pct ? "success" : "warning"
              }
              trend={d.trend?.savings_rate_pct}
              testId="financial-snapshot-savings"
            />
            <StatCell
              icon={Shield}
              label="Emergency fund"
              value={`${(d.emergency_fund?.months_covered ?? 0).toFixed(1)}mo`}
              subvalue={`of ${d.emergency_fund?.goal_months ?? 6}mo`}
              progressPct={Math.min(100, d.emergency_fund?.progress_pct ?? 0)}
              tone={
                (d.emergency_fund?.progress_pct ?? 0) >= 100
                  ? "success"
                  : (d.emergency_fund?.progress_pct ?? 0) >= 50
                    ? "primary"
                    : "warning"
              }
              testId="financial-snapshot-emergency"
            />
            <StatCell
              icon={Landmark}
              label="Retirement"
              value={`${Math.round(d.retirement?.readiness_score ?? 0)}%`}
              subvalue={
                d.retirement?.years_until_retirement != null
                  ? `${d.retirement.years_until_retirement}y to go`
                  : "readiness"
              }
              progressPct={Math.min(100, d.retirement?.readiness_score ?? 0)}
              tone={
                (d.retirement?.readiness_score ?? 0) >= 80
                  ? "success"
                  : (d.retirement?.readiness_score ?? 0) >= 50
                    ? "primary"
                    : "warning"
              }
              testId="financial-snapshot-retirement"
            />
            <StatCell
              icon={Target}
              label="FI progress"
              value={`${Math.round(d.fi?.progress_pct ?? 0)}%`}
              subvalue={
                d.fi?.years_remaining != null && d.fi.years_remaining > 0
                  ? `${d.fi.years_remaining}y left`
                  : d.fi?.years_remaining === 0
                    ? "Achieved 🎉"
                    : "long road"
              }
              progressPct={Math.min(100, d.fi?.progress_pct ?? 0)}
              tone={
                (d.fi?.progress_pct ?? 0) >= 100
                  ? "success"
                  : (d.fi?.progress_pct ?? 0) >= 50
                    ? "primary"
                    : "warning"
              }
              testId="financial-snapshot-fi"
            />
          </div>

          {/* Retirement gap alert (only when gap > 0) */}
          {d.retirement?.gap != null && d.retirement.gap > 0 && (
            <div
              className="mt-4 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/[0.06] p-3 text-xs"
              data-testid="financial-snapshot-gap-alert"
            >
              <span className="font-mono uppercase tracking-widest text-[10px] text-[hsl(var(--warning))] mr-1.5">
                Gap
              </span>
              You&apos;re projected to fall short of retirement by{" "}
              <span className="font-mono font-semibold text-foreground">
                {compactFmt(d.retirement.gap)}
              </span>
              . Open the financial plan to close it.
            </div>
          )}

          {/* CTA */}
          <div className="mt-4">
            <Button
              size="sm"
              onClick={() => nav("/app/financial")}
              variant="outline"
              className="gap-1.5 w-full sm:w-auto"
              data-testid="financial-snapshot-open"
            >
              Open financial plan <ArrowRight size={13} strokeWidth={2} />
            </Button>
          </div>
        </>
      )}
    </Widget>
  );
};

const TONE_BAR = {
  success: "bg-[hsl(var(--success))]",
  primary: "bg-primary",
  warning: "bg-[hsl(var(--warning))]",
};

const StatCell = ({ icon: Icon, label, value, subvalue, progressPct, tone, trend, testId }) => (
  <div
    className="rounded-lg border border-border bg-secondary/30 p-3 space-y-1.5"
    data-testid={testId}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
        <Icon size={12} strokeWidth={2} className="flex-shrink-0" />
        <span className="text-[10px] font-mono uppercase tracking-widest truncate">
          {label}
        </span>
      </div>
      {trend && (
        <TrendPill delta={trend.delta} format="percent" size="xs" />
      )}
    </div>
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-display font-black text-xl text-foreground">{value}</span>
      <span className="text-[10px] text-muted-foreground truncate">{subvalue}</span>
    </div>
    <div className="h-1 rounded-full bg-secondary overflow-hidden">
      <div
        className={`h-full rounded-full ${TONE_BAR[tone] || TONE_BAR.primary}`}
        style={{ width: `${Math.max(2, progressPct || 0)}%` }}
      />
    </div>
  </div>
);
